/*!
 * doTERRA Web-UI
 * http://www.doterra.com
 *
 * Copyright 2019 doTERRA Holdings, LLC
 * All Rights Reserved
 */
import React, {useContext, useEffect, useReducer} from 'react';
import {paymentMethods} from '../utils/payments';
import {useModalsMutator, useSiteState} from '../contexts/page';
import {getMarketInfo} from '../contexts/market';
import {useCustomerState} from '../contexts/customer';
import {ErrorModal, ModalContainer, SuccessModal, useModals} from '../components/modals';
import {get, post} from "../utils/rmi";
import {useJson} from "../utils/json";
import {getUnique} from '../../shared/utils/payments';
import {text} from '../utils/i18n';

const SELECT_PAYMENT = 'SELECT_PAYMENT';
const SELECT_MULTI_LINES_PAYMENT = 'SELECT_MULTI_LINES_PAYMENT';
const ADD_PAYMENT = 'ADD_PAYMENT';
const EDIT_PAYMENT = 'EDIT_PAYMENT';
const REMOVE_PAYMENT = 'REMOVE_PAYMENT';
const ERROR_PAYMENT = 'ERROR_PAYMENT';
const SET_PAYMENTS = 'SET_PAYMENTS';
const CHANGE_PAYMENT_MARKET = 'CHANGE_PAYMENT_MARKET';
const CRM_ADD_PAYMENT = 'CRM_ADD_PAYMENT';
const CRM_REMOVE_PAYMENT = 'CRM_REMOVE_PAYMENT';
const SET_PAYMENT_SAVE = 'SET_PAYMENT_SAVE';
const SET_ADD_NEW_PAYMENT = 'SET_ADD_NEW_PAYMENT';
const CRM_ACCEPT_PAYMENT_TERMS = 'CRM_ACCEPT_PAYMENT_TERMS';
const CRM_SET_NEXT_PAYMENT_LINE_ID = 'CRM_SET_NEXT_PAYMENT_LINE_ID';
const CRM_UPDATE_SELECTED_PAYMENT_DATE = 'CRM_UPDATE_SELECTED_PAYMENT_DATE';

const PaymentsStateContext = React.createContext();
const PaymentsDispatchContext = React.createContext();

const getPaymentById = (payments, id) => {
    return id ? (payments || []).find(payment => payment.id === id) : undefined;
};

const getPaymentByCode = (payments, id) => {
    return id ? (payments || []).find(payment => payment.code === id) : undefined;
};

const getPaymentAddresses = (payments = [])=> {

    const addressInfo = payments.filter(payment => (payment.type !== 'CASH' && payment.type !== 'CHECK'
        && payment.type !== 'ARBALANCE' && payment.type !== 'INSTORE') || payment.billingAddress).map(payment => payment.billingAddress);
    return getUnique(addressInfo, "addressBookNickname");
};

const sortPayments = (payments) => {
    if (payments.length < 2) {
        return payments;
    }
    payments.sort((payment1,payment2) => {
        return payment2.defaultPayment ? 1 : -1;
    });
    return payments;
};

const PaymentsReducer = (state, action) => {
    switch (action.type) {
        case SET_PAYMENTS: {
            const { payments, uniqueAddressInfo, context } = action.payload;
            if (context !== null && context === 'crm')
                {return {...state, payments: payments, uniqueAddressInfo };}
            return {...state, payments: sortPayments(payments), uniqueAddressInfo };
        }
        case CHANGE_PAYMENT_MARKET: {
            return {...state, uniqueAddressInfo: action.payload};
        }
        case SELECT_PAYMENT: {
            const { payment, cartPaymentId } = action.payload;
            if (cartPaymentId) {
                return {...state, cartPaymentId};
            }
                return {...state, editTarget: payment};


        }
        case SELECT_MULTI_LINES_PAYMENT: {
            const { selectedPayment, selectedPaymentPanelId } = action.payload;
            const crmPaymentSelectedList = state.crmPaymentSelectedList.map(payment => payment.crmPaymentLineID === selectedPaymentPanelId ? {...payment, selectedPayment: selectedPayment}:payment);
            return {...state, crmPaymentSelectedList};
        }
        case ADD_PAYMENT: {
            const payment = action.payload;
            let updateCartPaymentId;
            if (action.updateCartId) {
                updateCartPaymentId = payment.id;
            }
            const addedPaymentandOldPayments = [...state.payments, payment];

            // Grab the previously set payment that was default and set it to false if the
            // incoming payment's default is set to true
            state.payments.forEach(prevPayment =>
                payment.defaultPayment === true ? prevPayment.defaultPayment = false : prevPayment);

            const uniqueAddressInfo = getPaymentAddresses(addedPaymentandOldPayments);
            return {...state, isAddNewPayment:true, addedNewPayment:payment, payments: sortPayments(addedPaymentandOldPayments), editTarget: null, cartPaymentId: updateCartPaymentId, uniqueAddressInfo};
        }
        case EDIT_PAYMENT: {
            const payment = action.payload;

            // Grab the previously set payment that was default and set it to false if the
            // incoming payment's default is set to true
            state.payments.forEach(prevPayment =>
                payment.defaultPayment === true ? prevPayment.defaultPayment = false : prevPayment);

            // Combine the object of the payment that you edited with the new payment details based on their id
            const editedPayments = state.payments.map(prevPayment => prevPayment.id === payment.id ?
                {...prevPayment, ...payment}: prevPayment);

            const uniqueAddressInfo = getPaymentAddresses(editedPayments);
            return {...state, payments: sortPayments(editedPayments), editTarget: null, uniqueAddressInfo};
        }
        case REMOVE_PAYMENT: {
            const paymentId = action.payload;
            let { payments } = state;
            const paymentsAfterRemoveDeleted = payments.filter(payment => payment.id !== paymentId);
            return {...state, payments: paymentsAfterRemoveDeleted, editTarget: null };
        }
        case ERROR_PAYMENT: {
            return {...state};
        }
        case CRM_ADD_PAYMENT: {
            const {payment, crmPaymentLineID} = action.payload;
            const checkedPaymentLineList = (state.crmPaymentSelectedList || []).filter(payment=>payment.crmPaymentLineID === crmPaymentLineID);
            if (checkedPaymentLineList && checkedPaymentLineList.length > 0)
                {return {... state, isAddNewPayment:false};}
            return {...state, isAddNewPayment:false, crmPaymentSelectedList: [...state.crmPaymentSelectedList, {payment, selectedPayment: payment, crmPaymentLineID}]};
        }
        case CRM_REMOVE_PAYMENT: {
            const removedPaymentId = action.payload;

            const crmPaymentSelectedList = state.crmPaymentSelectedList.filter(payment=>(payment.crmPaymentLineID !== removedPaymentId));
            return {...state, crmPaymentSelectedList};
        }
        case SET_PAYMENT_SAVE: {
            const isPaymentSave = action.payload;
            return {...state, isPaymentSave};
        }
        case SET_ADD_NEW_PAYMENT: {
            const isAddNewPayment = action.payload;
            return {...state, isAddNewPayment};
        }
        case CRM_ACCEPT_PAYMENT_TERMS: {
            const acceptPaymentTerms = action.payload;
            return {...state, acceptPaymentTerms};
        }
        case CRM_SET_NEXT_PAYMENT_LINE_ID: {
            const crmNextPaymentLineId = action.payload;
            return {...state, crmNextPaymentLineId};
        }
        case CRM_UPDATE_SELECTED_PAYMENT_DATE: {

            const {expMonth, expYear, paymentLineID} = action.payload;
            const crmPaymentSelectedList = (state.crmPaymentSelectedList || []).map((p)=>{

                if (p.selectedPayment && p.crmPaymentLineID === paymentLineID) {
                    p.selectedPayment.expiryMonth = expMonth;
                    p.selectedPayment.expiryYear = expYear;
                }

                return p;
            });

            return {...state, crmPaymentSelectedList};
        }

        default: {
            throw new Error(`[PaymentsReducer] Unsupported action type: ${action.type}.`);
        }
    }
};



export const PaymentsProvider = (props) => {
    const { context, orderType, address, cartPaymentId, activePaymentMethods, children, cartMarketType, isRetail, isOrderConfirmationPage, isSubscriptionsPage, lrpSubType, ccNonce } = props;
    console.log('activePaymentMethods', activePaymentMethods);
    const { uid } = useCustomerState();
    const { contextPath } = useSiteState();
    const { modals, showModal, hideModal } = useModals();
    const [ state, dispatch] = useReducer(PaymentsReducer, {
        context,
        payments: [],
        editTarget: null,
        methods: paymentMethods.filter(data => activePaymentMethods.includes(data.method)),
        uniqueAddressInfo: address ? [address] : [],
        showModal,
        hideModal,
        crmPaymentSelectedList: [],
        cartPaymentId,
        cartMarketType,
        isPaymentSave: true,
        isAddNewPayment: false,
        acceptPaymentTerms: true,
        addedNewPayment: null,
        crmNextPaymentLineId: 1,
        isRetail,
        isOrderConfirmationPage,
        ccNonce,
        isSubscriptionsPage
    });

    const hide = () => {
        hideModal();
    };

    useEffect(() => {
        if (context !== 'express-checkout') {
            let paymanturl = `${contextPath}/payments/${uid}`;
            if ((context === 'crm') && !((orderType && orderType.toUpperCase() === 'LRP') && lrpSubType !== 'INSTANT'))
                {paymanturl = `${contextPath}/payments/${uid}/crm`;}

            if (uid) {
                requestAnimationFrame(() => {
                    get(paymanturl).then(results => {
                        const payments = results;
                        const uniqueAddressInfo = address ? [address] :getPaymentAddresses(payments);

                        dispatch({type: SET_PAYMENTS,
                            payload: {
                                payments,
                                methods: paymentMethods.filter((method) => activePaymentMethods.includes(method.method)),
                                uniqueAddressInfo,
                                context
                            }
                        });
                    });
                });
            }
        }
    }, [uid]);

    return (
        <PaymentsStateContext.Provider value={state}>
            <PaymentsDispatchContext.Provider value={dispatch}>
                {children}
                <ModalContainer modals={modals} close={hide}/>
            </PaymentsDispatchContext.Provider>
        </PaymentsStateContext.Provider>
    );
};


export const usePaymentsState = () => {
    const context = useContext(PaymentsStateContext);
    if (context === undefined) {
        throw new Error('usePaymentsState must be used within a PaymentsProvider.');
    }
    const { payments, cartPaymentId, editTarget , isOrderConfirmationPage } = context;

    const getCartPayment = () => {
        if(isOrderConfirmationPage && isOrderConfirmationPage === 'true'){
            return getPaymentByCode(payments, cartPaymentId);
        }
        return getPaymentById(payments, cartPaymentId);
    };
    const isEditingCartPayment = () => {
        if (!cartPaymentId || !editTarget) {
            return undefined;
        }
        let cartPayment = getCartPayment();
        return cartPayment && cartPayment.id === editTarget.id;
    };
    return {...context, getCartPayment, isEditingCartPayment};
};


const updateCartPayment = (dispatch, uid, payments, id, showGlobalModal, hideGlobalModal) => {
    showLoadingSpinner();
    post(`${contextPath}/payments/${uid}/cart/${id}`).then(result => {
        if (result === id) {
            const payment = getPaymentById(payments, id);
            dispatch({type: SELECT_PAYMENT, payload: {payment, cartPaymentId: payment.id}});
            hideLoadingSpinner();
        } else {
            const modalName = 'select-payment-error';
            showGlobalModal(modalName, <ErrorModal text={result.paymentErrorMsgs} close={() => hideGlobalModal(modalName)}/>);
            hideLoadingSpinner();
        }
    }, error => {
        hideLoadingSpinner();
    });
};

const updateSubscriptionCartPayment = (dispatch, uid, payments, id, showGlobalModal, hideGlobalModal, isCRM, subscriptionCode, csrfToken, setSubscriptionPayment) => {
  showLoadingSpinner();
  let updateSubscriptionSettingsPath = contextPath + '/subscription/settings';
  if (isCRM) {
    updateSubscriptionSettingsPath = contextPath + '/crm/subscription/settings';
  }

  post(`${updateSubscriptionSettingsPath}/payments/${uid}/lrptemplate/${subscriptionCode}/${id}${csrfToken ? '?CSRFToken='+ csrfToken : ''}`).then(result => {
    if (result.id === id) {
      const payment = getPaymentById(payments, id);
      dispatch({type: SELECT_PAYMENT, payload: {payment, cartPaymentId: payment.id}});
      hideLoadingSpinner();
      setSubscriptionPayment(result);
    } else {
      const modalName = 'select-payment-error';
      showGlobalModal(modalName, <ErrorModal text={result.paymentErrorMsgs} close={() => hideGlobalModal(modalName)}/>);
      hideLoadingSpinner();
    }
  }, error => {
    hideLoadingSpinner();
  });
};


export const usePaymentsMutator = () => {
    const { payments, isPaymentSave } = usePaymentsState();
    const dispatch = useContext(PaymentsDispatchContext);
    const { context, showModal, hideModal } = useContext(PaymentsStateContext);
    const { showGlobalModal, hideGlobalModal } = useModalsMutator();
    const { uid } = useCustomerState();

    if (!dispatch) {
        throw new Error('usePaymentsMutator must be used within a PaymentsProvider');
    }

    const selectCartPayment = (id) => {
        updateCartPayment(dispatch, uid, payments, id, showGlobalModal, hideGlobalModal);
    };

    const selectSubscriptionCardPayment = (id, isCRM, subscriptionCode, csrfToken, setSubscriptionPayment) => {
      updateSubscriptionCartPayment(dispatch, uid, payments, id, showGlobalModal, hideGlobalModal, isCRM, subscriptionCode, csrfToken, setSubscriptionPayment);
    };

    const changePaymentMarket = (market) => {
        const addressInfo = payments.filter(payment => payment.billingAddress.country.isocode === market).map(payment => payment.billingAddress);
        const uniqueAddressInfo = getUnique(addressInfo,"addressBookNickname");
        dispatch({type: CHANGE_PAYMENT_MARKET, payload:  uniqueAddressInfo});
    };

    const selectEditPayment = (id) => {
        const payment = getPaymentById(payments, id);
        dispatch({type: SELECT_PAYMENT, payload: {payment}});
    };

    const selectMultiLinesPayment = (id, selectedPaymentPanelId) => {
        const selectedPayment = getPaymentById(payments, id);
        dispatch({type: SELECT_MULTI_LINES_PAYMENT, payload: {selectedPayment, selectedPaymentPanelId}});
    };


    const handleNonceFromFollowAnalytics = (event) => {
        const digitalWalletPayload = event.detail.paymentDataReceived;
        digitalWalletPayload.makeDefault = document.getElementById("makeDefault") ? document.getElementById("makeDefault").checked : true;
        let succeeded = addPayment(digitalWalletPayload, null);
        event.detail.paymentDataRecievedCallback(succeeded);
    };

    const addPaymentHelp = (payment, subscriptionCode, csrfToken) => {
      showLoadingSpinner();
      let addContext = '';
      let updateCartId = true;
      let apiPath = 'payments';
      if (context === 'modal') {
        addContext = '?updateCart=true';
      } else if (context === 'subscriptionSettings' && subscriptionCode) {
        addContext = '?updateCart=true&subscriptionCode='+subscriptionCode;
      }else if (context === 'enrollment') {
        addContext = '?enrollmentCart=true';
      } else if (context === 'crm') {
        addContext = '?isPaymentSave=' + isPaymentSave;
        updateCartId = false;
      } else if (context === 'crm-subscription' && subscriptionCode) {
        addContext = csrfToken ? '?isPaymentSave=' + isPaymentSave + '&subscriptionCode='+subscriptionCode + '&CSRFToken='+ csrfToken : '?isPaymentSave=' + isPaymentSave;
        updateCartId = false;
      } else if (context === 'profile') {
        addContext = '?isFromProfile=' + true;
        updateCartId = false;
      } else if (context === 'express-checkout') {
        apiPath = 'expresscheckout';
      }

      if(payment && !(Object.keys(payment).length === 0) && !payment.digitalWalletType) {
        let marketRegion = getMarketInfo(payment.market);
        let regexExp = new RegExp(marketRegion[1].postalCode.pattern);
        if (!(regexExp.test(payment.billingZip))) {
          dispatch({type: ERROR_PAYMENT});
          const modalName = 'add-payment-error';
          showGlobalModal(modalName, <ErrorModal text={text('form.addressBookZip.error')} close={() => hideGlobalModal(modalName)}/>);
          hideLoadingSpinner();
          return false;
        }
      }

      return post(`${contextPath}/${apiPath}/${uid}/add${addContext}`, JSON.stringify(payment)).then(result => {
        if (!result.paymentErrorMsgs) {
          if (result.redirectURL) {
            window.location.replace(`${contextPath}${result.redirectURL}`);
          } else if (result.paymentInfoData) {

            const { parseJson } = useJson();
            if (result.isLRPPayment && result.isLRPPayment === 'true')
            {updateCartId = true;}

            const paymentInfoDataInJSON = parseJson(result.paymentInfoData);

            dispatch({type: ADD_PAYMENT, payload: paymentInfoDataInJSON, updateCartId});

            if (context === 'crm' || context === 'profile') {
              const modalName = 'add-payment-success';
              showGlobalModal(modalName, <SuccessModal text={result.successMsgs}
                                                       close={() => hideGlobalModal(modalName)}/>);
            } else if(context === 'subscriptionSettings' && paymentInfoDataInJSON.method === 'digital-wallet') {
              const titleText = localText["text.lrp.titleSavedThisOrder"];
              const messageText = localText['text.lrp.savedThisOrder'];
              const closeText = localText["popup.close"];
              const modalName = 'add-digital-wallet-modal';

              showGlobalModal(modalName, <SuccessModal text={messageText} title={titleText} closeText={closeText} close={() => hideGlobalModal(modalName)} autoClose={5000}/>);
            }
            hideLoadingSpinner();
            return true;
          }
        } else {
          dispatch({type: ERROR_PAYMENT});
          const modalName = 'add-payment-error';
          showGlobalModal(modalName, <ErrorModal text={result.paymentErrorMsgs} title={result.paymentErrorTitle} close={() => {
            hideGlobalModal(modalName);
            if (result.redirectURL) {
              let newLocation = result.redirectURL;
              if (!newLocation.startsWith('http')) {
                newLocation = `${contextPath}${result.redirectURL}`;
              }
              window.location.replace(newLocation);
            }
          }}/>);
          hideLoadingSpinner();
          return false;
        }
      }, error => {
        dispatch({type: ERROR_PAYMENT, payload:error});
        hideLoadingSpinner();
        return false;
      });
    };

    const addPayment = (payment, subscriptionCode) => {
        addPaymentHelp(payment, subscriptionCode);
    };

    const editPaymentHelp = (payment, subscriptionCode, csrfToken) => {
        showLoadingSpinner();

      let editContext = '';
      if (context === 'modal' || context === 'subscriptionSettings') {
        editContext = '?updateCart=true';
      } else if (context === 'enrollment') {
        editContext = '?enrollmentCart=true';
      } else if (context === 'crm-subscription') {
        editContext = csrfToken ? '?CSRFToken='+ csrfToken : '';
      } else if (context === 'profile') {
        editContext = csrfToken ? '?isFromProfile=true&CSRFToken='+ csrfToken : '?isFromProfile=true';
      }

        return post(`${contextPath}/payments/${uid}/edit/${payment.id}${editContext}`, JSON.stringify(payment)).then(result => {
            if (result.redirectURL) {
                window.location.replace(`${contextPath}${result.redirectURL}`);
            } else if (result.paymentInfoData) {
                const { parseJson } = useJson();
                dispatch({type: EDIT_PAYMENT, payload: parseJson(result.paymentInfoData)});
                const modalName = 'edit-payment-success';
                showGlobalModal(modalName, <SuccessModal text={result.successMsgs} close={() => hideGlobalModal(modalName)} autoClose={true}/>);
                hideLoadingSpinner();
                return true;
            } else {
                dispatch({type: ERROR_PAYMENT});
                const modalName = 'edit-payment-error';
                showGlobalModal(modalName, <ErrorModal text={result.paymentErrorMsgs} close={() => hideGlobalModal(modalName)}/>);
                hideLoadingSpinner();
                return false;
            }
        }, (error) => {
            dispatch({type: ERROR_PAYMENT});
            hideLoadingSpinner();
            return false;
        });

    };

    const editPayment = (payment) => {
      editPaymentHelp(payment);
    };

    const removePayment = (id) => {
        showLoadingSpinner();
        return get(`${contextPath}/payments/${uid}/remove/${id}`).then(result => {
            dispatch({type: REMOVE_PAYMENT, payload: id});
            const modalName = 'remove-payment-success';
            showGlobalModal(modalName, <SuccessModal text={result} close={() => hideGlobalModal(modalName)} autoClose={true}/>);
            hideLoadingSpinner();
            return true;
        }, error => {
            dispatch({type: ERROR_PAYMENT});
            hideLoadingSpinner();
            return false;
        });
    };

    const crmAddPayment = (payment, crmPaymentLineID) => {
        dispatch({type: CRM_ADD_PAYMENT, payload: {payment, crmPaymentLineID}});
    };

    const crmRemovePayment = (paymentId) => {
        dispatch({type: CRM_REMOVE_PAYMENT, payload: paymentId});
    };

    const setPaymentSave = (isPaymentSave) => {
        dispatch({type: SET_PAYMENT_SAVE, payload: isPaymentSave});
    };

    const setAddNewPayment = (isAddNewPayment) => {
        dispatch ({type: SET_ADD_NEW_PAYMENT, payload: isAddNewPayment});
    };

    const setAcceptPaymentTerms = (acceptPaymentTerms) => {
        dispatch ({type: CRM_ACCEPT_PAYMENT_TERMS, payload: acceptPaymentTerms});
    };

    const setCRMNextPaymentLineId = (nextId) => {
        dispatch ({type: CRM_SET_NEXT_PAYMENT_LINE_ID, payload: nextId});
    };

    const checkPaymentType = (payment, paymentType) => {
        const paymentChecks = [payment.type !== undefined && payment.type !== paymentType, payment.digitalWalletType !== undefined && payment.digitalWalletType !== paymentType, payment.cardType !== undefined];
        console.log(paymentChecks.some(element => element));
        return paymentChecks.some(element => element);
    };

    const setDefaultPayment = (payment) => {

        payment.defaultPayment = true;
        return post(`${contextPath}/payments/${uid}/setdefaultpayment/${payment.id}`, JSON.stringify(payment)).then(result => {
            if (result.paymentInfoData) {
                const { parseJson } = useJson();
                dispatch({type: EDIT_PAYMENT, payload: parseJson(result.paymentInfoData)});
                return true;
            }
                dispatch({type: ERROR_PAYMENT});
                return false;

        }, (error) => {
            dispatch({type: ERROR_PAYMENT});
            return false;
        });

    };

    const updatePayment = (payment) => {

        dispatch({type: EDIT_PAYMENT, payload: payment});
    };

    const updateSelectedPaymentDate = (expMonth, expYear, paymentLineID) => {

        dispatch({type: CRM_UPDATE_SELECTED_PAYMENT_DATE, payload: {expMonth, expYear, paymentLineID}});
    };

    return { selectCartPayment, selectMultiLinesPayment, selectEditPayment, handleNonceFromFollowAnalytics, addPayment, editPayment, removePayment, changePaymentMarket, crmAddPayment, crmRemovePayment, setPaymentSave, setAddNewPayment, setDefaultPayment, setAcceptPaymentTerms, setCRMNextPaymentLineId, updatePayment, updateSelectedPaymentDate, showModal, hideModal, selectSubscriptionCardPayment, addPaymentHelp, editPaymentHelp};

};


export const testables = { getPaymentById, getPaymentAddresses};
